import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import LegalLayout from "@/components/LegalLayout";
import { Trash2, Mail, LogIn } from "lucide-react";

export default function AccountDeletion() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <LegalLayout title="Suppression de compte" lastUpdate="13 février 2026">
      <p>Conformément à l&apos;<strong>article 17 du RGPD</strong> (droit à l&apos;effacement / droit à l&apos;oubli), tu peux demander la suppression de ton compte Fripri à tout moment.</p>

      <h2>1. Comment supprimer ton compte ?</h2>

      <h3>Option A — Suppression en autonomie (recommandée, immédiate)</h3>
      <ol>
        <li>Connecte-toi sur <Link to="/login">fripri.com</Link></li>
        <li>Va dans <strong>Mon espace</strong> (icône en haut à droite)</li>
        <li>Trouve la carte <strong>« Mes données · RGPD »</strong></li>
        <li>Clique sur le bouton <strong>« Supprimer mon compte »</strong></li>
        <li>Tape <code>SUPPRIMER</code> dans le champ de confirmation et valide</li>
      </ol>

      {user ? (
        <p>
          <button
            onClick={() => navigate("/dashboard?action=delete")}
            className="btn btn-primary"
            data-testid="goto-dashboard-delete-btn"
          >
            <Trash2 size={16} strokeWidth={3}/> Aller au tableau de bord
          </button>
        </p>
      ) : (
        <p>
          <Link to="/login?next=/dashboard" className="btn btn-primary" style={{ color: "#fff" }} data-testid="login-to-delete-btn">
            <LogIn size={16} strokeWidth={3}/> Se connecter pour supprimer mon compte
          </Link>
        </p>
      )}

      <h3>Option B — Demande par email (si tu n&apos;arrives plus à te connecter)</h3>
      <p>Envoie un email à <a href="mailto:direction@fripri.com?subject=Demande%20de%20suppression%20de%20compte%20Fripri"><strong>direction@fripri.com</strong></a> avec :</p>
      <ul>
        <li>Objet : <em>« Demande de suppression de compte »</em></li>
        <li>L&apos;adresse email associée à ton compte Fripri</li>
        <li>Une preuve d&apos;identité (capture de pièce d&apos;identité, partielle, avec photo + nom suffisent)</li>
      </ul>
      <p>Nous traiterons ta demande sous <strong>30 jours maximum</strong> (article 12 du RGPD), généralement sous 72 heures.</p>

      <h2>2. Que se passe-t-il quand tu supprimes ton compte ?</h2>
      <p>Conformément à l&apos;<strong>article 17 du RGPD</strong> et au principe de minimisation des données :</p>
      <ul>
        <li>✅ <strong>Anonymisé immédiatement</strong> : ton nom, ton email, ta photo, ton téléphone, tes informations Stripe</li>
        <li>✅ <strong>Supprimé immédiatement</strong> : tes annonces en cours, ton panier, ta wishlist, tes notifications, tes abonnements push</li>
        <li>✅ <strong>Effacé</strong> : tes messages internes sont remplacés par « Message supprimé »</li>
        <li>⚠️ <strong>Conservé 10 ans</strong> (anonymisé) : ton historique de commandes et de paiements, conformément à l&apos;article L123-22 du Code de commerce et au Code général des impôts (obligation comptable). Aucune information personnelle n&apos;y est conservée, uniquement un identifiant technique anonyme</li>
      </ul>

      <h2>3. Quelles données sont conservées et pourquoi ?</h2>
      <table>
        <thead><tr><th>Donnée</th><th>Durée</th><th>Base légale</th></tr></thead>
        <tbody>
          <tr><td>Identité, contact, profil</td><td>Supprimé immédiatement</td><td>—</td></tr>
          <tr><td>Annonces actives</td><td>Supprimées immédiatement</td><td>—</td></tr>
          <tr><td>Historique commandes (anonymisé)</td><td>10 ans</td><td>Obligation comptable et fiscale</td></tr>
          <tr><td>Logs techniques (anonymisés)</td><td>12 mois max</td><td>Sécurité, lutte contre la fraude</td></tr>
        </tbody>
      </table>

      <h2>4. C&apos;est définitif ?</h2>
      <p><strong>Oui</strong>, la suppression est irréversible. Tu ne pourras pas récupérer tes annonces, tes messages, ni ton historique d&apos;avis. Si tu souhaites revenir sur Fripri plus tard, tu devras créer un nouveau compte avec une adresse email différente (l&apos;ancien email étant anonymisé).</p>

      <h2>5. Questions ou problème ?</h2>
      <p>Contacte-nous à <a href="mailto:direction@fripri.com">direction@fripri.com</a> — réponse garantie sous 30 jours conformément au RGPD.</p>

      <hr style={{margin: "2rem 0", border: "none", borderTop: "1px solid rgba(0,0,0,0.1)"}}/>
      <p style={{fontSize: "0.85rem", color: "var(--color-text-muted)"}}>
        🛡 Cette page existe pour respecter les obligations de transparence du <strong>RGPD</strong>, du <strong>Google Play Store</strong> (Account Deletion Policy) et de l&apos;<strong>Apple App Store</strong> (App Store Review Guideline 5.1.1(v)).
      </p>
    </LegalLayout>
  );
}
